/*
This code defines a JavaScript class called StickyATC with several methods to set up and handle interactions with a 
sticky "Add to Cart" (ATC) component on a web page. The class is constructed with two parameters: sectionId and productId, 
and it performs various tasks associated with the ATC functionality. Here's an overview of what each method does:

Constructor (constructor(sectionId, productId)): Initializes the StickyATC instance with the provided sectionId and 
productId, then calls the init method to set up the sticky ATC component and sync various elements.

init(): Calls several methods to initialize the sticky ATC component and sync elements, as follows:

- setupStickyATC(): Configures the click event handlers for the "Buy Now" and "Close" buttons on the sticky ATC component.
- syncATCButtons(): Synchronizes the state of "Add to Cart" buttons on the page.
- syncVariantRadiosAndDropdowns(): Synchronizes variant radios and dropdowns on the page.
- injectRecharge(): Injects ReCharge widgets based on the productId and sectionId.
- setupStickyATC(): Configures the click event handlers for the "Buy Now" and "Close" buttons within the sticky ATC component. When the "Buy Now" button is clicked, it adds a CSS class to open the component, and when the "Close" button is clicked, it removes the class to close the component.

1) injectRecharge(): Sets an interval to check if the ReChargeWidget is available. When ReChargeWidget is available, 
it creates a widget with specific product and section IDs. Additionally, it calls the syncReChargeOptions() method and 
clears the interval once the widget is created.

2) syncATCButtons(): Synchronizes the state of "Add to Cart" buttons on the page. It checks if the first ATC button is not
 disabled, and if so, it removes the disabled attribute from all "Add to Cart" buttons on the page after a delay.

3) syncVariantRadiosAndDropdowns(): Synchronizes the variant radios and dropdowns on the page. When a change event occurs 
in a variant dropdown, it updates the values in other dropdowns and radios to maintain consistency. It also triggers a 
change event on the corresponding variant dropdowns to handle any related interactions.

4) syncReChargeOptions(): Synchronizes ReCharge options. It sets intervals to wait for the ReChargeWidget and its options 
to load. Once loaded, it handles changes in ReCharge options, such as radio button selections and dropdown changes. It 
also ensures the consistency of selling plan input values on the page.

How to initialise:
1) add script to page:
<script src="{{ 'sticky-atc.min.js' | asset_url }}"></script>

2) initialise widget:
<script>
  document.addEventListener('DOMContentLoaded', function () {
      const sectionId = '{{ section.id }}';// Set the section ID here
      const productId = {{ product.id }}; // Set the product ID here
      new StickyATC(sectionId, productId);
    });
</script>
*/
class StickyATC {
  constructor(sectionId, productId) {
    this.sectionId = sectionId;
    this.productId = productId;
    this.init();
  }

  init() {
    this.setupStickyATC();
    this.syncATCButtons();
    this.syncVariantRadiosAndDropdowns();
    this.injectRecharge();
  }

  setupStickyATC() {
    const stickyAtc = document.querySelector(
      `#shopify-section-${this.sectionId}.section--sticky-atc`
    );
    if (stickyAtc) {
      const buyNowButton = stickyAtc.querySelector(".button--buy-now");
      const closeButton = stickyAtc.querySelector(".button--close");

      buyNowButton.addEventListener("click", () => {
        stickyAtc.classList.add("section--sticky-atc--open");
      });

      closeButton.addEventListener("click", () => {
        stickyAtc.classList.remove("section--sticky-atc--open");
      });
    }
  }

  injectRecharge() {
    const interval = setInterval(() => {
      if (window.ReChargeWidget) {
        // console.log("creating widget!!!", this.productId, this.sectionId);
        window.ReChargeWidget.createWidget({
          productId: this.productId,
          injectionParent: `#product-form-${this.sectionId}${this.productId}`,
        });
        this.syncReChargeOptions();
        clearInterval(interval);
      }
    }, 500);
  }

  syncATCButtons() {
    const firstATCButton = document.querySelector(
      `form[data-productid='${this.productId}'] button[name='add'].product-form__submit`
    );
    console.log("firstATCButton", firstATCButton);
    if (firstATCButton) {
      setTimeout(() => {
        if (firstATCButton.disabled === false) {
          const allButtons = document.querySelectorAll(
            `form[data-productid='${this.productId}'] button[name='add'].product-form__submit`
          );
          for (const button of allButtons) {
            button.disabled = false;
          }
        }
      }, 1000);
    }
  }

  syncVariantRadiosAndDropdowns() {
    // Implement your logic for syncing variant radios and dropdowns here
    const inputNameIds = document.querySelectorAll(
      `form[data-productid='${this.productId}'] input[name='id']`
    );
    const variantDropdowns = document.querySelectorAll(
      `form[data-productid='${this.productId}'] select.variant-selector`
    );

    for (const input of inputNameIds) {
      input.addEventListener("change", (e) => {
        for (const inp of inputNameIds) {
          inp.value = e.target.value;
        }
      });
    }
    const variantRadios = document.querySelectorAll(
      `form[data-productid='${this.productId}'] input.variant-radio`
    );

    for (const dropdown of variantDropdowns) {
      dropdown.addEventListener("change", (e) => {
        for (const drop of variantDropdowns) {
          drop.value = e.target.value;
        }
        for (const radio of variantRadios) {
          if (radio.value === e.target.value) {
            radio.checked = true;
          }
        }

        setTimeout(() => {
          if (dropdown) {
            const sourceButton = dropdown.closest(
              `form[data-productid='${this.productId}']").querySelector('button[name="add"].product-form__submit`
            );
            synATCButtons(sourceButton);
          }
        }, 500);
      });
    }

    for (const radio of variantRadios) {
      radio.addEventListener("change", (e) => {
        if (e.target.checked) {
          for (const dropdown of variantDropdowns) {
            dropdown.value = e.target.value;
            const changeEvent = new Event("change", { bubbles: true });
            dropdown.dispatchEvent(changeEvent);
          }
        }

        const sourceButton = radio.closest(
          `form[data-productid='${this.productId}']").querySelector('button[name="add"].product-form__submit`
        );
        setTimeout(() => {
          // console.log("sourceButton", sourceButton);
          synATCButtons(sourceButton);
        });
      });
    }
  }

  syncReChargeOptions() {
    // Implement your logic for syncing ReCharge options here
    const int = setInterval(() => {
      // Wait until ReCharge has loaded
      if (window.ReChargeWidget) {
        const i = setInterval(() => {
          // Wait until ReCharge options have loaded
          const rechargeRadios = document.querySelectorAll(
            ".rc_widget__option__input"
          );

          if (rechargeRadios && rechargeRadios.length > 0) {
            for (const radio of rechargeRadios) {
              radio.addEventListener("change", (e) => {
                if (e.target.checked) {
                  for (const rad of rechargeRadios) {
                    if (rad.value === e.target.value) {
                      rad.checked = true;
                      rad.closest("label").classList.add("rc-option--active");
                    } else {
                      rad
                        .closest("label")
                        .classList.remove("rc-option--active");
                    }
                  }
                }
              });
            }

            const rechargeFreqOptions = document.querySelectorAll(
              "select.rc_widget__option__plans__dropdown"
            );

            if (rechargeFreqOptions && rechargeFreqOptions.length > 0) {
              for (const item of rechargeFreqOptions) {
                item.addEventListener("change", (e) => {
                  for (const i of rechargeFreqOptions) {
                    i.value = e.target.value;
                  }

                  //get closest form, then get selling_plan hidden input value. Assign this value to any selling plan inputs on the page

                  const productForms = document.querySelectorAll(
                    `form[data-productid='${this.productId}']`
                  );

                  for (const form of productForms) {
                    const sellPlanInput = form.querySelector(
                      "input[name='selling_plan']"
                    );

                    if (!sellPlanInput) {
                      const newInput = document.createElement("input");
                      newInput.type = "hidden";
                      newInput.name = "selling_plan";
                      newInput.value = e.target.value;
                      form.appendChild(newInput);
                    } else {
                      sellPlanInput.value = e.target.value;
                    }
                  }
                });
              }
            }

            clearInterval(i);
          }
        }, 500);

        clearInterval(int);
      }
    }, 500);
  }
}
